import React, { useContext, useRef, useEffect, useState } from 'react';
import Collapse from 'antd/lib/collapse';
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { MDForm, FormContext } from '../../metadata/MDForm';
import { ProductInfoContext } from '../../data/ApplicationDataSource';
import { DeclaredComponent } from '../../DynamicComponent';
import { getLineProductSetupContext } from '../line/LineForm';
import { StepsValidationStatusContext } from '../../../context/StepsValidationStatusProvider';
import { DriversAndVehiclesDataReportContext } from '../../../context/DriversAndVehiclesDataReportProvider';
import { RemoveBeanBtn } from '../RemoveBeanBtn';
import { ModelContext } from '../../metadata/ModelSource';
import { getMappedValue } from '../../metadata/mappings';
import { GlobalStateContext } from '../../../context/GlobalStateProvider';

const { Panel } = Collapse;

export function DriverListForm({ forms, lineCd }) {
  const applicationModel = useContext(ModelContext);
  const { isDesktop } = useContext(GlobalStateContext)

  const drivers = applicationModel.getValue('drivers');

  if (!drivers) {
    return null;
  }

  const driversRef = useRef([]);

  let { productSetup } = useContext(ProductInfoContext);
  const { scrollIndex, visitedDrivers, setVisitedDrivers } = useContext(StepsValidationStatusContext);
  const { expandDrivers } = useContext(DriversAndVehiclesDataReportContext);
  const [oneDriver, setOneDriver] = useState(true);
  const [activeKeys, setActiveKeys] = useState([]);
  const [dataPrefillKeys, setDataPrefillKeys] = useState([]);

  const setActiveDriverKeys = (key)=>{
    /*
      The idea here is by default, all the data prefill drivers will be expanded.
      After all data prefill drivers been collapsed or user add a new driver, only one driver will be
      expanded at same time.
     */
    if (activeKeys && activeKeys.length === 1){
      //If only 1 driver is active, then check if all data prefill drivers been collapsed or not
      if (dataPrefillKeys.length === 0){
        activeKeys[0] === key ? setActiveKeys([]): setActiveKeys(new Array(key));
      }else {
        activeKeys[0] === key ? setActiveKeys([]): setActiveKeys([...activeKeys, key]);
      }
    } else {
      const index = activeKeys.indexOf(key);
      let newActiveKeys = [...activeKeys];
      if(index !== -1){
        newActiveKeys.splice(index,1);
      }else {
        newActiveKeys = [...newActiveKeys, key];
      }
      setActiveKeys(newActiveKeys);
    }
    setDataPrefillDriverKeys(key);
  }

  const setDataPrefillDriverKeys = (key)=>{
    const index = dataPrefillKeys.indexOf(key);
    let keys = [...dataPrefillKeys];
    index !== -1 ? keys.splice(index, 1) : null
    setDataPrefillKeys(keys);
  }

  const getDefaultDriverKeys = ()=>{
    const defaultActiveKeys = [];
    for (let i in drivers){
      const d = drivers[i];
      if (d.id && d.id.indexOf('Prefill') >= 0 && d.status === 'Active'){
        defaultActiveKeys.push(i.toString())
      }
    }
    return defaultActiveKeys;
  }

  const lineProductSetupContext = getLineProductSetupContext(
    productSetup,
    applicationModel,
    lineCd
  );

  useEffect(() => {
    setActiveDriverKeys((drivers.length - 1).toString());
    if (!isDesktop && (drivers.length !== 1 && !drivers[0].nameInfo.givenName)) {
      return scrollTo({
        top: 400,
        behavior: 'smooth'
      })
    }
    scrollTo(0, 0)
  }, [drivers.length])

  useEffect(() => {
    if (
        drivers.length === 1 &&
        visitedDrivers === false &&
        !applicationModel.data.drivers[0].nameInfo
    ) {
      setVisitedDrivers(true)
    }

    const defaultActiveKeys = getDefaultDriverKeys()
    if (defaultActiveKeys && defaultActiveKeys.length > 0){
      setActiveKeys(defaultActiveKeys);
    }else {
      const driverIndex = (drivers.length - 1).toString();
      setActiveDriverKeys(driverIndex);
    }
    setDataPrefillKeys(defaultActiveKeys);
  }, [])

  useEffect(() => {
    const activeDrivers = drivers.filter(driver => {
      return driver.status !== 'Deleted';
    });
    setOneDriver(activeDrivers.length === 1);
  }, [applicationModel]);

  const scrollToDriverRef = ref => {
    ref ? window.scrollTo(0, ref.offsetTop) : null;
  };

  useEffect(() => {
    if (scrollIndex !== undefined && driversRef.current.length === drivers.length ) {
      scrollToDriverRef(driversRef.current[scrollIndex]);
      setActiveDriverKeys(scrollIndex.toString())
    }
  }, [scrollIndex]);

  useEffect(() => {
    if (!expandDrivers){
      setDataPrefillKeys([]);
      setActiveKeys([(drivers.length - 1).toString()]);
    }
  }, [drivers.length])

  useEffect(() => {
    if (!isDesktop && (drivers.length !== 1 && !drivers[0].nameInfo.givenName)) {
      return scrollTo({
        top: 400,
        behavior: 'smooth'
      })
    }
    scrollTo(0, 0)
  }, [activeKeys])

  const Header = ({ driver, driverIndex, oneDriver }) => {
    const { partyTypeCd } = driver;
    const type = partyTypeCd === 'DriverParty' ? 'Driver' : 'Non-Driver';
    const props = {
      style: {
        marginLeft: 'auto'
      },
      path: 'drivers',
      proto: {
        partyTypeCd
      },
      // eslint-disable-next-line quotes
      prefix: `drivers[${driverIndex}]`,
      type
    };
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: oneDriver ? 'flex-start' : 'center',
          alignContent: 'center',
          width:"100%"
        }}
      >
        <span
          style={{ fontSize: '18px', margin: 0, alignSelf: 'center' }}
          className="bold"
          aria-level={'2'}
          role={'heading'}
          onClick={() => setActiveDriverKeys(driverIndex.toString())}
        >
          {type} Detail{' '}
          {driver.nameInfo ? `- ${driver.nameInfo.givenName !== undefined ? driver.nameInfo.givenName : ''}` : ''}
        </span>
        {oneDriver ? null : <RemoveBeanBtn {...props} />}
      </div>
    );
  };

  const panelDetails = (form, driverProductSetupContext, driver, key, index) => {
    return (
      <Panel
        key={index}
        header={
          <Header driver={driver} driverIndex={index} oneDriver={oneDriver} />
        }
        forceRender={true}
      >
        <div
          key={key}
          ref={element => {
            driversRef.current[index] = element;
          }}
        >
          <MDForm
            key={key}
            prefix={`drivers[${index}]`}
            model={applicationModel}
            productSetupContext={driverProductSetupContext}
            // extendedAttributes={ApplicationExtendedAttributes}
            presentationMetadata={form}
            dataPrefill={
              drivers.length === 1 &&
              visitedDrivers === false &&
              !applicationModel.data.drivers[0].nameInfo ?
                driverPrefill : undefined
            }
          >
            <DeclaredComponent declaration={form} {...key} />
          </MDForm>
        </div>
      </Panel>
    );
  };

  return (
    <Collapse
      expandIcon={props => {
        const { panelKey, isActive } = props;
        return (
            isActive
                ? <DownOutlined onClick={() => setActiveDriverKeys(panelKey)} style={{marginTop:"10px"}}/>
                : <RightOutlined onClick={() => setActiveDriverKeys(panelKey)} style={{marginTop:"10px"}}/>
        );
      }}
      bordered={false}
      activeKey={activeKeys}
      accordion={false}
    >
      {drivers.map((driver, i) => {
        // Since drivers sent to the api cannot be removed from the application, this will prevent them from showing on the UI
        if (driver.status === 'Deleted') return null;

        const key = driver.id || driver._tempId;

        const form = forms[driver.partyTypeCd];

        if (!form) {
          throw 'form is not defined for driver party ' + driver.partyTypeCd;
        }

        const driverProductSetupContext =
          lineProductSetupContext[
            driver.partyTypeCd === 'NonDriverParty'
              ? 'productNonDrivers'
              : 'productDrivers'
          ];

        if (!driverProductSetupContext) {
          throw 'no product setup found for driver party ' + driver.partyTypeCd;
        }

        return panelDetails(form, driverProductSetupContext, driver, key, i);
      })}
    </Collapse>
  );
}


const driverPrefill = {
  'Driver.GivenName': data => getMappedValue(data, 'insured.partyInfo[0].nameInfo.givenName'),
  'Driver.Surname': data => getMappedValue(data, 'insured.partyInfo[0].nameInfo.surname')
};